import React from 'react';

/**
 * Placeholder shapes for content that is still loading.
 *
 * Every member page used to replace its whole content region with a centred
 * spinner, so each navigation blanked the page and then repainted it. The
 * heading and breadcrumb of a page are known before any read returns, which
 * means the blank was never necessary: render the frame immediately and put
 * these where the data will land.
 *
 * Honours prefers-reduced-motion via the `.melio-skeleton` rule in index.css
 * rather than animate-pulse, so the same guard covers it as the rest of the
 * app's motion.
 */

type SkeletonProps = {
  className?: string;
  /** Inline width, e.g. '60%' or 220. Useful for ragged text lines. */
  width?: string | number;
  height?: string | number;
  radius?: string | number;
};

export default function Skeleton({ className = '', width, height, radius }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={`melio-skeleton block ${className}`}
      style={{ width, height, borderRadius: radius }}
    />
  );
}

/** A paragraph's worth of lines, last one short so it reads as text. */
export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <span className={`block space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          height={12}
          radius={4}
          width={index === lines - 1 ? '55%' : '100%'}
        />
      ))}
    </span>
  );
}

/** Stands in for a SectionCard while its data loads. */
export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div
      className="rounded-3xl border p-5 md:p-6"
      style={{ borderColor: 'var(--m-line)', backgroundColor: 'var(--m-surface)' }}
    >
      <Skeleton width={90} height={10} radius={4} className="mb-3" />
      <Skeleton width="45%" height={20} radius={6} className="mb-4" />
      <SkeletonText lines={lines} />
    </div>
  );
}

/**
 * The standard member loading body: a few cards where the page's sections will
 * be. Pair it with the page's real PageHeader, which needs no data.
 */
export function SkeletonPageBody({ cards = 3 }: { cards?: number }) {
  return (
    <div className="space-y-6" role="status" aria-label="Loading">
      {Array.from({ length: cards }).map((_, index) => (
        <SkeletonCard key={index} lines={index === 0 ? 2 : 3} />
      ))}
    </div>
  );
}
